import { Injectable, Inject } from '@angular/core';
import { Observable, concat, of, Subject, combineLatest } from 'rxjs';
import { catchError, map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { HttpHelper } from '@app/common/http-helper';
import { CacheService } from '@app/services/cache.service';
import { NewsPage } from '@app/models/news-page';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { UserChannelTerm } from '@app/models/user-channel-term';
import { SearchPostQuery, SearchPostQueryRequest } from '@app/models/search-post-query';
import { SearchService } from '@app/services/search.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { CultureService } from '@app/services/culture.service';
import { CafcassEnvironment } from '../../environments/cafcassEnvironment';
import { HttpParams, HttpClient } from '@angular/common/http';
import { ListItem } from '@microsoft/microsoft-graph-types';
import { ChannelService } from '@app/services/channel.service';

@Injectable({
  providedIn: 'root'
})
export class ComplimentsService {

  constructor(@Inject(CUSTOMERENVIRONMENT) private environment: CafcassEnvironment,
              private cache: CacheService,
              private featureSettingsStoreService: FeatureSettingsStoreService,
              private searchService: SearchService,
              private cultureService: CultureService,
              private httpHelper: HttpHelper,
              private http: HttpClient,
              private channelService: ChannelService) { }

  private readonly cacheKeyPrefix = 'ComplimentService';

  public useCache = true;

  private getModernComplimentPostsFromSearch$ = new Map<string, Observable<Array<NewsPage>>>();
  private reloadGetModernComplimentPostsFromSearch$ = new Subject<void>();
  private getModernPersonalNewsFromComplimentSite$: Observable<Array<NewsPage>>;
  private readonly translationFields = '_SPIsTranslation,_SPTranslatedLanguages,_SPTranslationSourceItemId,_SPTranslationLanguage';

  getPersonalModernComplimentsFromComplimentSiteFromSearch(amount: number, channels?: Array<UserChannelTerm>): Observable<Array<NewsPage>> {

    const channelsMngProp = this.environment.featureSettings.managedPropertyConfig.newsChannelManagedProperty;

    let channelQuery = '';
    if (channels?.length > 0) {
      const joinedChannels = channels.map(c => channelsMngProp + ':' + c.ChannelTermId).join(' OR ');
      channelQuery = `(${joinedChannels})`;
    }

    // Searching for pages where channel is empty comes from this suggestion: https://fivep.com.au/kierans-tech-talk-searching-for-empty-managed-metadata-columns/
    channelQuery = `(${channelQuery} OR NOT(${channelsMngProp}:G*))`;

    let languageFilterQuery = '';
    if (this.featureSettingsStoreService.state.enableModernSPNewsLanguageFilter) {
      languageFilterQuery = ` AND ((SPTranslationLanguage:${this.cultureService.getLocaleWithSuffix().toLowerCase()} AND SPIsTranslation:true) OR (NOT(SPIsTranslation:true) AND NOT(SPTranslatedLanguages:${this.cultureService.getLocaleWithSuffix().toLowerCase()})))`;
    }

    const fromSite = `SiteName:${this.environment.complimentsWallSiteUrl}`;
    const query = `(ContentTypeId: 0x0101009D1CB255DA76424F860D91F20E6C4118* AND PromotedState: 2${languageFilterQuery}) AND ${fromSite} AND ${channelQuery}`;

    return this.getModernNewsFromSearch(amount, query);

  }

  getModernNewsFromSearch(amount: number, query: string): Observable<Array<NewsPage>> {
    const params = `${amount.toString()}-${query}`;

    if (this.getModernComplimentPostsFromSearch$.has(params)) {
       return this.getModernComplimentPostsFromSearch$.get(params);
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-${'getModernComplimentPostsFromSearch'}-${params}`;

      const postQuery = new SearchPostQuery({
        request: new SearchPostQueryRequest({
          Querytext: query,
          RowLimit: amount,
          EnableSorting: true,
          TrimDuplicates: true,
          SortList: [
            {
              'Property': 'FirstPublishedDate',
              'Direction': 1
            }
          ],
          SelectProperties: [
            'Title',
            'EditorOwsUser',
            'ModifiedBy',
            'LastModifiedBy',
            'Description',
            'Path',
            'PictureThumbnailURL',
            'LastModifiedTime',
            'FirstPublishedDate',
            'SPWebUrl',
            'SPIsTranslation',
            'SPTranslationLanguage',
            'SPTranslationSourceItemId',
            'SPTranslatedLanguages',
            'SiteName',
            this.environment.featureSettings.managedPropertyConfig.newsAmountOfCommentsManagedProperty,
            this.environment.featureSettings.managedPropertyConfig.newsAmountOfLikesManagedProperty,
            this.environment.featureSettings.managedPropertyConfig.newsChannelManagedProperty,
            this.environment.featureSettings.managedPropertyConfig.newsIsTopNewsManagedProperty,
          ],
          Properties: [
            {
              Name: 'EnableDynamicGroups',
              Value: {
                BoolVal: 'True',
                QueryPropertyValueTypeIndex: 3
              }
            } // include results form private groups - https://www.techmikael.com/2017/01/retrieving-search-results-from-private.html
          ]
        })
      });

      this.getModernComplimentPostsFromSearch$.set(params, this.searchService.sendPostSearchQuery(postQuery)
        .pipe(
          map(data => {
            const parsedResults = data.map(item => {
              return NewsPage.fromSearchResult(item,
                this.environment.featureSettings.managedPropertyConfig.newsAmountOfCommentsManagedProperty,
                this.environment.featureSettings.managedPropertyConfig.newsAmountOfLikesManagedProperty,
                this.environment.featureSettings.managedPropertyConfig.newsChannelManagedProperty,
                this.environment.featureSettings.managedPropertyConfig.newsLanguageManagedProperty);
            });
            this.useCache && this.cache.set(cacheKey, parsedResults);
            return parsedResults;
          }),
          catchError(this.httpHelper.handleError({ operation: 'getModernComplimentPostsFromSearch', result: new Array<NewsPage>(), rethrow: true, cacheKey: cacheKey })),
          takeUntil(this.reloadGetModernComplimentPostsFromSearch$),
          shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<any>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getModernComplimentPostsFromSearch$.get(params));
        }
      }
      return this.getModernComplimentPostsFromSearch$.get(params);
    }
  }

  getMyModernPersonalNewsFromComplimentSite(userChannelTerms$: Observable<Array<UserChannelTerm>>) {
    if (this.getModernPersonalNewsFromComplimentSite$) {
      return this.getModernPersonalNewsFromComplimentSite$;
    } else {
      const cacheKey = `${this.cacheKeyPrefix}-${'getMyModernPersonalNewsFromGraph'}`;
      const newsCenterSite =  this.environment.complimentsWallSiteUrl;
      const listsRequest = `${newsCenterSite}/_api/v2.0/sharePoint/getBaseItemByUrl(url='/SitePages')/items`;
      const selectFields = `Title,BannerImageUrl,BoolField_Channel,Description,_CommentCount,_LikeCount,PromotedState,FirstPublishedDate${this.featureSettingsStoreService.state.enableModernSPNewsLanguageFilter ? ',' + this.translationFields : ''}`;
      let httpParams = new HttpParams()
        .append('expand', `fields(select=${selectFields})`)
        .append('orderby', 'fields/FirstPublishedDate desc')
        .append('select', 'fields,id,webUrl')
        .append('top', '100');
      if (this.featureSettingsStoreService.state.enableModernSPNewsLanguageFilter) {
        // Filter reads like this: Get only non-top news where the page has been translated to our language or the apge is not translated and has no translations to our language.
        httpParams = httpParams.append('filter', `(fields/PromotedState eq 2) and ((fields/_SPTranslationLanguage eq '${this.cultureService.getLocaleWithSuffix().toLowerCase()}' and fields/_SPIsTranslation eq 1) or (fields/_SPIsTranslation ne 1 and fields/_SPTranslatedLanguages ne '${this.cultureService.getLocaleWithSuffix().toLowerCase()}'))`);
      } else {
        // Filter reads like this: Get only non-top top news
        httpParams = httpParams.append('filter', `(fields/PromotedState eq 2)`);
      }

      const httpCall$ = this.http
        .get<any>(listsRequest, {
          params: httpParams,
          headers: {
            'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly'
          }
        });
      const getMyModernPersonalNewsFromSite$ = combineLatest([userChannelTerms$, httpCall$])
        .pipe(
          map(([userChannelTerms, listItemResult]) => {
            const listItems = listItemResult.value as ListItem[];
            return listItems.map(item => {
              return new NewsPage({
                Title: item.fields['Title'],
                PublishedDate: item.fields['FirstPublishedDate'],
                Channels: item.fields['BoolField_Channel'],
                Picture: item.fields['BannerImageUrl'] ? item.fields['BannerImageUrl'].Url : null,
                Description: item.fields['Description'],
                CommentsCount: item.fields['_CommentCount'] ? item.fields['_CommentCount'] : 0,
                LikesCount: item.fields['_LikeCount'] ? item.fields['_LikeCount'] : 0,
                Path: item.webUrl,
                IsSharePointNews: true
              });
            }).filter(i => {
                const channelTerms = i.Channels;
                if (channelTerms && channelTerms.length > 0) {
                  let found = true;
                  const matchingChannels = i.Channels.filter(c => {
                    found = userChannelTerms.some(ct => ct.ChannelTermId === c.TermGuid);
                    if (found) {
                      return c;
                    }
                  });
                  if (matchingChannels && matchingChannels.length > 0) {
                    return i;
                  }
                } else {
                  return i;
                }
              });
          }));

      this.getModernPersonalNewsFromComplimentSite$ = getMyModernPersonalNewsFromSite$
        .pipe(
          tap(data => {
            this.useCache && this.cache.set(cacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getMyModernPersonalNewsFromSite', result: new Array<NewsPage>(), rethrow: true, cacheKey: cacheKey })),
          shareReplay({ bufferSize: 1, refCount: true })
        );

      if (this.useCache) {
        const cachedData = this.cache.get<Array<NewsPage>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getModernPersonalNewsFromComplimentSite$);
        }
      }
      return this.getModernPersonalNewsFromComplimentSite$;
    }
  }

}
